import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import TaskTable from '../../components/Tasks/TaskTable';
import TaskKanban from '../../components/Tasks/TaskKanban';
import CreateModal from '../../components/Tasks/CreateModal';
import EditModal from '../../components/Tasks/EditModal';
import TaskDetails from '../../components/Tasks/TaskDetails';
import styles from '../../components/Tasks/Tasks.module.css';
import { Button, Radio, message, Space, Checkbox, Popover, Tooltip } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { columnLabels, STATUSES } from '../../constants/taskColumnLabels';
import {
  fetchTasks,
  fetchTaskById,
  createTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  updateTaskStatusOptimistic,
  revertTaskStatus
} from '../../redux/tasksSlice';
import { fetchUsers } from '../../redux/userOrganisationsSlice';
import { fetchCandidates } from '../../redux/candidatesSlice';
import { fetchVacancies } from '../../redux/vacanciesSlice';
import { getFilesFromForm, uploadTaskFiles } from '../../services/taskFiles';


const allColumnKeys = Object.keys(columnLabels);

const defaultTaskColumns = [
  'id',
  'candidate_id',
  'vacancy_names',
  'description',
  'deadline',
  'assignee_id',
  'status',
  'actions'
];

const TasksPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { orgId, role } = useSelector((state) => state.auth);
  const { list: tasks, isLoading: tasksLoading, totalPages, totalItems } = useSelector((state) => state.tasks);
  const { list: users, isLoading: usersLoading } = useSelector((state) => state.userOrganisations);
  const { list: candidatesFromRedux = [] } = useSelector((state) => state.candidates);
  const { list: vacanciesFromRedux = [] } = useSelector((state) => state.vacancies);

  const [viewMode, setViewMode] = useState('list');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(defaultTaskColumns);
  const [selectedStatuses, setSelectedStatuses] = useState(Object.keys(STATUSES));
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [statusesDropdownOpen, setStatusesDropdownOpen] = useState(false);

  // Состояние пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Состояние фильтров
  const [filters, setFilters] = useState({});

  const loading = tasksLoading || usersLoading;

  const candidates = React.useMemo(() => {
    const candidatesMap = new Map();

    tasks?.forEach(task => {
      if (task.candidate && task.candidate.id) {
        if (!candidatesMap.has(task.candidate.id)) {
          candidatesMap.set(task.candidate.id, task.candidate);
        }
      }
    });

    candidatesFromRedux?.forEach(candidate => {
      if (candidate && candidate.id) {
        candidatesMap.set(candidate.id, candidate);
      }
    });

    return Array.from(candidatesMap.values());
  }, [tasks, candidatesFromRedux]);

  const vacancies = React.useMemo(() => {
    const vacanciesMap = new Map();

    tasks?.forEach(task => {
      if (task.vacancy && task.vacancy.id) {
        if (!vacanciesMap.has(task.vacancy.id)) {
          vacanciesMap.set(task.vacancy.id, task.vacancy);
        }
      }
    });

    vacanciesFromRedux?.forEach(vacancy => {
      if (vacancy && vacancy.id) {
        vacanciesMap.set(vacancy.id, vacancy);
      }
    });

    return Array.from(vacanciesMap.values());
  }, [tasks, vacanciesFromRedux]);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        dispatch(fetchUsers()).unwrap(),
      ]);
    } catch (error) {
      message.error('Ошибка загрузки данных');
    }
  };

  const loadCandidatesAndVacancies = async () => {
    try {
      await Promise.all([
        dispatch(fetchCandidates({ skip: 0, limit: 1000, org_id: orgId })).unwrap(),
        // dispatch(fetchVacancies({ skip: 0, limit: 1000, org_id: orgId })).unwrap(),
      ]);
    } catch (error) {
      message.error('Ошибка загрузки данных для формы');
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [dispatch]);

  useEffect(() => {
    const skip = (currentPage - 1) * pageSize;

    // Преобразуем фильтры для бэкенда в нормальном формате
    const backendFilters = {};
    Object.keys(filters).forEach(key => {
      const value = filters[key];

      if (value === null || value === undefined || value === '') {
        return;
      }

      const backendKey = key;

      if (Array.isArray(value)) {
        const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
        if (filteredArray.length > 0) {
          backendFilters[backendKey] = filteredArray.join(',');
        }
      } else if (typeof value === 'string' || typeof value === 'number') {
        backendFilters[backendKey] = value;
      } else if (typeof value === 'boolean') {
        backendFilters[backendKey] = value;
      }
    });

    dispatch(fetchTasks({ skip, limit: pageSize, filters: backendFilters }));
  }, [dispatch, currentPage, pageSize, filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const openTaskId = urlParams.get('openTask');

    if (openTaskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === parseInt(openTaskId));
      if (task) {
        setSelectedTask(task);
        setIsDetailsModalOpen(true);
      }
    }
  }, [location.search, tasks, navigate]);

  const handleCreate = async (formData) => {
    try {
      const result = await dispatch(createTask({ task: formData })).unwrap();
      const newTaskId = typeof result === 'number' ? result : (result?.data || result?.id);

      const files = getFilesFromForm(formData.materials);
      if (newTaskId && files.length > 0) {
        try {
          await uploadTaskFiles(newTaskId, files);
        } catch (_) {
          message.error('Не удалось загрузить некоторые файлы');
        }
      }
      message.success('Задача создана');
      const skip = (currentPage - 1) * pageSize;
      const backendFilters = {};
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value)) {
          const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
          if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
        } else if (typeof value === 'string' || typeof value === 'number') {
          backendFilters[key] = value;
        }
      });
      await dispatch(fetchTasks({ skip, limit: pageSize, filters: backendFilters })).unwrap();
      setIsCreateModalOpen(false);
    } catch (error) {
      message.error('Ошибка при создании задачи');
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await dispatch(updateTask({ id, organisationId: orgId, task: formData })).unwrap();
      const newFiles = getFilesFromForm(formData.materials);
      if (newFiles.length > 0) {
        try {
          await uploadTaskFiles(id, newFiles);
        } catch (_) {
          message.error('Не удалось загрузить некоторые файлы');
        }
      }
      message.success('Задача обновлена');
      await dispatch(fetchTaskById(id)).unwrap();
      setIsEditModalOpen(false);
    } catch (error) {
      message.error('Ошибка при обновлении задачи');
    }
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteTask({ id, organisationId: orgId })).unwrap();
      message.success('Задача удалена');
      const skip = (currentPage - 1) * pageSize;
      const backendFilters = {};
      Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value === null || value === undefined || value === '') return;
        if (Array.isArray(value)) {
          const filteredArray = value.filter(v => v !== null && v !== undefined && v !== '');
          if (filteredArray.length > 0) backendFilters[key] = filteredArray.join(',');
        } else if (typeof value === 'string' || typeof value === 'number') {
          backendFilters[key] = value;
        }
      });
      await dispatch(fetchTasks({ skip, limit: pageSize, filters: backendFilters })).unwrap();
    } catch (error) {
      message.error('Ошибка при удалении задачи');
    }
  };

  const handleView = (task) => {
    setSelectedTask(task);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (task) => {
    loadCandidatesAndVacancies();
    setSelectedTask(task);
    setIsEditModalOpen(true);
  };

  const handleStatusChange = async (item, newStatus, newIndex) => {
    const originalStatus = item.status;

    try {
      dispatch(updateTaskStatusOptimistic({ id: item.id, status: newStatus }));

      await dispatch(updateTaskStatus({
        id: item.id,
        organisationId: orgId,
        status: newStatus
      })).unwrap();

      message.success('Статус задачи обновлён');
      await dispatch(fetchTaskById(item.id)).unwrap();
    } catch (error) {
      console.error('Status update failed:', error);
      dispatch(revertTaskStatus({ id: item.id, originalStatus }));
      message.error('Ошибка при обновлении статуса');
    }
  };

  const handleCommentSubmit = async (id, comment) => {
    try {
      await dispatch(updateTask({ id, organisationId: orgId, task: { comment } })).unwrap();
      message.success('Комментарий сохранен');
    } catch (error) {
      message.error('Ошибка при сохранении комментария');
    }
  };

  return (
    <div className={styles.container} style={{ paddingBottom: '0px' }}>
      <div className={styles.controls}>
        <h1 style={{ margin: '0px', marginRight: 'auto', paddingRight: '20px' }}>Задачи</h1>
        <Space wrap>
          {viewMode === 'list' ? (
            <Tooltip title="Настроить отображение колонок в таблице">
              <Popover
                open={columnsDropdownOpen}
                onOpenChange={setColumnsDropdownOpen}
                content={
                  <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                    {allColumnKeys.map(key => (
                      <div key={key} style={{ padding: '4px 0' }}>
                        <Checkbox
                          checked={selectedColumns.includes(key)}
                          onChange={(e) => {
                            setSelectedColumns(prev => {
                              return prev.includes(key)
                                ? prev.filter(col => col !== key)
                                : [...prev, key];
                            });
                          }}
                        >
                          {columnLabels[key]}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
              >
                <Button icon={<SettingOutlined />}>
                  Настройка колонок
                </Button>
              </Popover>
            </Tooltip>
          ) : (
            <Tooltip title="Настроить отображение статусов в канбане">
              <Popover
                open={statusesDropdownOpen}
                onOpenChange={setStatusesDropdownOpen}
                content={
                  <div style={{ maxHeight: '70vh', overflowY: 'auto', minWidth: '200px' }}>
                    {Object.keys(STATUSES).map(key => (
                      <div key={key} style={{ padding: '4px 0' }}>
                        <Checkbox
                          checked={selectedStatuses.includes(key)}
                          onChange={(e) => {
                            setSelectedStatuses(prev => {
                              return prev.includes(key)
                                ? prev.filter(status => status !== key)
                                : [...prev, key];
                            });
                          }}
                        >
                          {STATUSES[key].title}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                }
                trigger="click"
                placement="bottomLeft"
              >
                <Button icon={<SettingOutlined />}>
                  Настройка канбана
                </Button>
              </Popover>
            </Tooltip>
          )}

          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            buttonStyle="solid"
          >
            <Tooltip title="Просмотр задач в виде таблицы">
              <Radio.Button value="list">Список</Radio.Button>
            </Tooltip>
            <Tooltip title="Просмотр задач в виде канбан-доски">
              <Radio.Button value="kanban">Канбан</Radio.Button>
            </Tooltip>
          </Radio.Group>

          <Tooltip title={role === 'employee' ? 'У вас нет прав для создания задач' : 'Создать новую задачу'}>
            <Button
              type="primary"
              onClick={() => {
                loadCandidatesAndVacancies();
                setIsCreateModalOpen(true);
              }}
              disabled={role === 'employee'}
            >
              Создать задачу
            </Button>
          </Tooltip>
        </Space>
      </div>
      <>
        {viewMode === 'list' ? (
          <TaskTable
            tasks={tasks}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            candidates={candidates}
            users={users}
            visibleColumns={selectedColumns}
            loading={loading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            filters={filters}
            onFiltersChange={setFilters}
          />
        ) : (
          <TaskKanban
            tasks={tasks}
            onStatusChange={handleStatusChange}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onTaskUpdated={() => fetchAllData()}
            candidates={candidates}
            users={users}
            selectedStatuses={selectedStatuses}
            STATUSES={STATUSES}
          />
        )}
      </>

      <CreateModal
        visible={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        candidates={candidates}
        users={users}
        vacancies={vacancies}
      />

      <EditModal
        visible={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onSubmit={handleUpdate}
        task={selectedTask}
        candidates={candidates}
        users={users}
        vacancies={vacancies}
      />

      <TaskDetails
        visible={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        task={selectedTask}
        onCommentSubmit={handleCommentSubmit}
        users={users}
        candidates={candidates}
        onEditTask={handleUpdate}
      />
      <Outlet />
    </div>
  );
};

export default TasksPage;